import React from 'react';
import { track } from '@amplitude/analytics-browser';
import {
  Alert,
  Box,
  Card,
  FileUploader,
  Icon,
  IconButton,
  Link,
  Text,
} from '@nimbus-ds/components';
import { FileIcon, ThumbnailIcon, TrashIcon } from '@nimbus-ds/icons';
import {
  MAX_CHARGEBACK_DOCUMENT_SIZE_BR,
  useTranslationWithPrefix,
} from 'commons/utils';
import { TrackingTags } from '../enums/TrackingTags.enum';
import UploadCardSkeleton from './UploadCardSkeleton';
import { useChargebackDocumentUploaderBR } from './useChargebackDocumentUploaderBR';

interface UploadCardProps {
  error: string;
  orderId: string;
  onSuccess: (key: string, duration: number) => void;
  onFileError: (error: string) => void;
  onErrorDismiss: () => void;
  onUploadError: () => void;
  onDelete: () => void;
}

function UploadCard({
  error,
  orderId,
  onSuccess,
  onFileError,
  onErrorDismiss,
  onUploadError,
  onDelete,
}: UploadCardProps) {
  const { t } = useTranslationWithPrefix('chargebackUpload');
  const { t: tCard } = useTranslationWithPrefix('chargebackUpload.uploadCard');

  const { isLoading, fileName, blobUrl, handleFileChange, handleFileDelete } =
    useChargebackDocumentUploaderBR({
      orderId,
      maxDocumentSize: MAX_CHARGEBACK_DOCUMENT_SIZE_BR,
      onSuccess,
      onFileError,
      onUploadError,
      onDelete,
    });

  const renderFileInput = () => {
    if (isLoading) {
      return <UploadCardSkeleton />;
    }

    if (fileName) {
      return (
        <Box display="flex" flexDirection="column" gap="2">
          <Box
            backgroundColor="neutral-background"
            borderRadius="2"
            boxShadow="2"
            width="100%"
            padding="4"
            display="flex"
            flexDirection="row"
            alignItems="center"
            gap="2"
          >
            <Box
              backgroundColor="primary-surface"
              borderStyle="solid"
              borderColor="primary-surfaceHighlight"
              borderWidth="1"
              borderRadius="2"
              paddingX="2"
              paddingY="1"
              display="flex"
              alignItems="center"
            >
              <Icon source={<FileIcon />} color="primary-interactive" />
            </Box>
            <Box flex="1" minWidth="0">
              <Text fontWeight="bold" color="neutral-textHigh">
                {fileName}
              </Text>
            </Box>
            <IconButton
              source={<TrashIcon />}
              size="2rem"
              onClick={handleFileDelete}
              aria-label={t('delete')}
            />
          </Box>
          {blobUrl && (
            <Link
              as="a"
              appearance="primary"
              href={blobUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() =>
                track(TrackingTags.nuvempago_cbks_view_document_click)
              }
            >
              <Icon source={<FileIcon size="small" />} color="currentColor" />
              {t('seeDocument')}
            </Link>
          )}
        </Box>
      );
    }

    return (
      <Box
        width="100%"
        display="flex"
        flexDirection="column"
        gap="2"
        height="72px"
      >
        <FileUploader
          aspectRatio="none"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) handleFileChange(file);
          }}
          placeholder={tCard('helperText')}
          accept="application/pdf"
          id="chargeback-defense"
          data-testid="file-input-chargeback-defense"
          height="180px"
        />
      </Box>
    );
  };

  return (
    <Card data-testid="chargeback-upload-upload-card">
      <Card.Header title={tCard('title')} />
      <Card.Body>
        <Box display="flex" flexDirection="column" gap="4">
          <Box
            display="flex"
            flexDirection="row"
            gap="2"
            alignItems="flex-start"
          >
            <Icon source={<ThumbnailIcon />} color="primary-interactive" />
            <Text>{tCard('instruction')}</Text>
          </Box>
          <Box
            display="flex"
            flexDirection="row"
            gap="2"
            alignItems="flex-start"
          >
            <Icon source={<FileIcon />} color="primary-interactive" />
            <Text>{tCard('fileFormat')}</Text>
          </Box>
          <Alert
            show={Boolean(error)}
            appearance="danger"
            title={tCard('errorTitle')}
            onRemove={onErrorDismiss}
          >
            {error}
          </Alert>
          {renderFileInput()}
        </Box>
      </Card.Body>
    </Card>
  );
}

UploadCard.Skeleton = UploadCardSkeleton;
export default UploadCard;
